/**
 * React hooks for real-time updates using the unified RealtimeClient.
 *
 * These hooks provide a simple, declarative interface for components
 * to subscribe to real-time updates with automatic cleanup and
 * optimal re-rendering.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { realtimeClient, RealtimeMessage, ConnectionState, ConnectionStats, SubscriptionOptions } from '../services/RealtimeClient';

/**
 * Hook to get the current connection state
 */
export function useRealtimeConnection() {
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    realtimeClient.getConnectionState()
  );
  const [stats, setStats] = useState<ConnectionStats>(realtimeClient.getStats());

  useEffect(() => {
    // Listen for connection state changes
    const unsubscribeConnection = realtimeClient.onConnectionStateChange(setConnectionState);

    // Update stats periodically
    const updateStats = () => setStats(realtimeClient.getStats());
    updateStats(); // Initial update
    const statsInterval = setInterval(updateStats, 5000);

    return () => {
      unsubscribeConnection();
      clearInterval(statsInterval);
    };
  }, []);

  const reconnect = useCallback(() => {
    realtimeClient.connect().catch(console.error);
  }, []);

  const disconnect = useCallback(() => {
    realtimeClient.disconnect();
  }, []);

  return {
    connectionState,
    stats,
    isConnected: connectionState === ConnectionState.CONNECTED,
    isConnecting: connectionState === ConnectionState.CONNECTING,
    reconnect,
    disconnect
  };
}

/**
 * Hook to subscribe to real-time updates for a specific entity
 */
export function useRealtimeSubscription(
  entityType: string,
  entityId: string,
  options: SubscriptionOptions = {}
) {
  const [messages, setMessages] = useState<RealtimeMessage[]>([]);
  const [lastMessage, setLastMessage] = useState<RealtimeMessage | null>(null);
  const messagesRef = useRef<RealtimeMessage[]>([]);

  useEffect(() => {
    // Skip subscription if entityType or entityId is not provided
    if (!entityType || !entityId) {
      return;
    }

    const handleMessage = (message: RealtimeMessage) => {
      // Filter by message types if specified
      if (options.messageTypes && !options.messageTypes.includes(message.type)) {
        return;
      }

      setLastMessage(message);
      setMessages(prev => {
        const updated = [...prev, message];
        messagesRef.current = updated;
        return updated;
      });
    };

    const unsubscribe = realtimeClient.subscribe(entityType, entityId, handleMessage, options);

    return unsubscribe;
  }, [entityType, entityId, JSON.stringify(options)]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setLastMessage(null);
    messagesRef.current = [];
  }, []);

  return {
    messages,
    lastMessage,
    clearMessages
  };
}

/**
 * Hook specifically for signal progress updates
 */
export function useSignalProgress(signalId: string) {
  const { messages, lastMessage } = useRealtimeSubscription(
    'signal',
    signalId,
    { messageTypes: ['signal.progress', 'signal.classified', 'signal.updated'] }
  );

  // Extract progress information from the latest message
  const progressInfo = lastMessage?.type === 'signal.progress'
    ? {
        stage: lastMessage.data?.stage,
        percent: lastMessage.data?.percent,
        status: lastMessage.data?.processing_status,
        startedAt: lastMessage.data?.startedAt,
        lastRealtimeAt: lastMessage.data?.lastRealtimeAt,
        elapsedSeconds: lastMessage.data?.elapsedSeconds,
        timestamp: lastMessage.timestamp || Date.now()
      }
    : null;

  const isProcessing = progressInfo?.stage && progressInfo.stage !== 'done';
  const isCompleted = lastMessage?.type === 'signal.classified' || progressInfo?.stage === 'done';

  return {
    progressInfo,
    isProcessing,
    isCompleted,
    lastMessage,
    allMessages: messages
  };
}

/**
 * Hook for organization-wide updates
 */
export function useOrganizationUpdates(organizationId: string, messageTypes?: string[]) {
  return useRealtimeSubscription('org', organizationId, { messageTypes });
}

/**
 * Hook for user-specific notifications
 */
export function useUserNotifications(userId: string) {
  return useRealtimeSubscription('user', userId, { messageTypes: ['user.notification'] });
}

/**
 * Hook to send real-time messages (for future use)
 */
export function useRealtimeSender() {
  const [connectionState] = useState(() => realtimeClient.getConnectionState());

  const sendMessage = useCallback((message: any) => {
    // This would require implementing a send method on RealtimeClient
    // For now, this is a placeholder for future functionality
    console.warn('Sending messages not yet implemented');
  }, []);

  return {
    sendMessage,
    canSend: connectionState === ConnectionState.CONNECTED
  };
}

/**
 * Hook for managing multiple subscriptions efficiently
 */
export function useRealtimeSubscriptions() {
  const subscriptionsRef = useRef<Set<() => void>>(new Set());

  const subscribe = useCallback((
    entityType: string,
    entityId: string,
    handler: (message: RealtimeMessage) => void,
    options?: SubscriptionOptions
  ) => {
    const unsubscribe = realtimeClient.subscribe(entityType, entityId, handler, options);
    subscriptionsRef.current.add(unsubscribe);

    return () => {
      unsubscribe();
      subscriptionsRef.current.delete(unsubscribe);
    };
  }, []);

  const unsubscribeAll = useCallback(() => {
    subscriptionsRef.current.forEach(unsubscribe => unsubscribe());
    subscriptionsRef.current.clear();
  }, []);

  useEffect(() => {
    return unsubscribeAll;
  }, [unsubscribeAll]);

  return {
    subscribe,
    unsubscribeAll
  };
}